import { Controller } from "@hotwired/stimulus"

// SECTION-SPY (phase 127, shared by the themes of 127–135): a contents list
// whose links name sections by `#<id>` marks the link of the section in view
// `aria-current="true"` — the last section of the list that intersects the band
// `rootMargin` leaves of the viewport. With `center` below 1024px the current
// link is scrolled to the middle of its row. Without
// IntersectionObserver the list stays a plain list of links.
export default class SectionSpyController extends Controller<HTMLElement> {
  static targets = ["link"]
  static values = { rootMargin: { type: String, default: "-35% 0px -55% 0px" }, center: { type: Boolean, default: false } }

  declare readonly linkTargets: HTMLAnchorElement[]
  declare readonly rootMarginValue: string
  declare readonly centerValue: boolean
  private observer: IntersectionObserver | null = null
  private readonly seen = new Set<Element>()

  connect(): void {
    if (!("IntersectionObserver" in window)) return
    this.observer = new IntersectionObserver(this.observed, { rootMargin: this.rootMarginValue })
    this.sections().forEach((section) => this.observer?.observe(section))
  }

  disconnect(): void {
    this.observer?.disconnect()
    this.observer = null
    this.seen.clear()
  }

  private readonly observed = (entries: IntersectionObserverEntry[]): void => {
    entries.forEach((entry) => (entry.isIntersecting ? this.seen.add(entry.target) : this.seen.delete(entry.target)))
    const current = this.sections().filter((section) => this.seen.has(section)).pop()
    if (!current) return
    this.linkTargets.forEach((link) => {
      if (link.hash !== `#${current.id}`) return link.removeAttribute("aria-current")
      link.setAttribute("aria-current", "true")
      if (!this.centerValue || window.innerWidth >= 1024) return
      const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
      link.scrollIntoView({ block: "nearest", inline: "center", behavior })
    })
  }

  private sections(): HTMLElement[] {
    return this.linkTargets.flatMap((link) => {
      const section = link.hash ? document.getElementById(decodeURIComponent(link.hash.slice(1))) : null
      return section ? [ section ] : []
    })
  }
}
